import { useEffect, useRef, useState } from 'react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import ErrorBoundary from '../components/ErrorBoundary';
import { useReveal, PageHero, PageCta } from './pageShared';
import './LegalPage.css';

function pad(value) {
  return String(value).padStart(2, '0');
}

function ChevronIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function TocList({ sections, activeId, onNavigate, className }) {
  return (
    <ol className={className}>
      {sections.map((section, index) => {
        const isActive = activeId === section.id;
        return (
          <li key={section.id}>
            <a
              href={'#' + section.id}
              className={'legal-toc-link' + (isActive ? ' is-active' : '')}
              aria-current={isActive ? 'location' : undefined}
              onClick={onNavigate}
            >
              <span className="legal-toc-num">{pad(index + 1)}</span>
              <span className="legal-toc-text">{section.title}</span>
            </a>
          </li>
        );
      })}
    </ol>
  );
}

function LegalPage({ eyebrow, title, leads, meta, sections, cta }) {
  const revealRef = useReveal();
  const detailsRef = useRef(null);
  const [activeId, setActiveId] = useState(sections[0].id);

  useEffect(() => {
    const root = revealRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;

    const nodes = Array.from(root.querySelectorAll('.legal-item'));
    if (!nodes.length) return undefined;

    const visible = new Set();
    const order = sections.map((section) => section.id);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.add(entry.target.id);
          } else {
            visible.delete(entry.target.id);
          }
        });
        const next = order.find((id) => visible.has(id));
        if (next) {
          setActiveId(next);
        }
      },
      { rootMargin: '0px 0px -55% 0px', threshold: 0 }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [sections]);

  const closeMobileToc = (event) => {
    if (event.target.closest('a') && detailsRef.current) {
      detailsRef.current.open = false;
    }
  };

  return (
    <ErrorBoundary>
      <div className="app page-root legal-page" ref={revealRef}>
        <SiteNav />
        <main className="page-main">
          <PageHero
            eyebrow={eyebrow}
            title={title}
            leads={leads}
            meta={meta}
            className="page-hero-legal"
          />
          <section className="legal-body">
            <div className="lp-container legal-layout">
              <aside className="legal-toc" aria-label="Table of contents">
                <p className="legal-toc-label">On this page</p>
                <TocList sections={sections} activeId={activeId} className="legal-toc-list" />
              </aside>

              <div className="legal-content">
                <details className="legal-toc-mobile" ref={detailsRef} onClick={closeMobileToc}>
                  <summary>
                    <span>On this page</span>
                    <ChevronIcon />
                  </summary>
                  <TocList
                    sections={sections}
                    activeId={activeId}
                    className="legal-toc-list-plain"
                  />
                </details>

                {sections.map((section, index) => (
                  <section
                    className="legal-item"
                    id={section.id}
                    key={section.id}
                    data-reveal="true"
                  >
                    <h2 className="legal-h2">
                      <span className="legal-index">{pad(index + 1)}</span>
                      <span>{section.title}</span>
                    </h2>
                    <div className="legal-prose">{section.body}</div>
                  </section>
                ))}
              </div>
            </div>
          </section>
          <PageCta {...cta} className="lp-section-tint" />
        </main>
        <SiteFooter />
      </div>
    </ErrorBoundary>
  );
}

export default LegalPage;
